import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { getApiErrorMessage } from '../lib/apiError';
import AuthService from '../services/auth.service';
import managerService, {
  type ApprovalQueueItem,
  type ApprovalType,
  type SlaSummary,
  type TeamCapacityItem,
} from '../services/manager.service';
import { fullName } from '../lib/fullName';
import { useRegisterWidget } from '../components/layout/RealtimeProvider';
import PageHeader from '../components/layout/PageHeader';
import ReassignAgentModal from '../components/manager/ReassignAgentModal';
import TimeOffManager from '../components/scheduling/TimeOffManager';
import WaitingForMe from '../components/staff/WaitingForMe';
import { colors } from '../styles/theme';
import { readerLocale, t } from '../i18n/language';
import { formatCents } from '../lib/amount';

interface DepositStatusSummary {
  status: string | null;
  /** ISO 4217 code for this bucket. Totals are split per-currency upstream
   *  so the widget never renders a cross-currency sum. */
  currency: string | null;
  count: number;
  total_amount: string;
}

interface DashboardMetrics {
  total_pending_onboarding: number;
  total_pending_deposits: number;
  deposits_summary: DepositStatusSummary[];
}

interface PendingOnboardingCase {
  case_id: string;
  client_id: string;
  client_first_name: string;
  client_last_name: string;
  client_email: string;
  state: string;
  assigned_agent_id: string | null;
  assigned_agent_first_name: string | null;
  assigned_agent_last_name: string | null;
  created_at: string;
}

const APPROVAL_TYPE_LABELS: Record<ApprovalType, { icon: string; label: string }> = {
  deposit: { icon: '💰', label: 'DEPOSIT' },
  withdrawal: { icon: '🏦', label: 'WITHDRAWAL' },
  withdrawal_request: { icon: '⏰', label: 'WITHDRAWAL REQUEST' },
  contract: { icon: '📄', label: 'CONTRACT' },
  onboarding: { icon: '👥', label: 'ONBOARDING' },
  escalated_callback: { icon: '📞', label: 'ESCALATED' },
  unassigned_callback: { icon: '🙋', label: 'NO AGENT' },
};

type AgeTone = 'danger' | 'warning' | 'info';

function ageBadge(createdAt: string): { label: string; tone: AgeTone } {
  const created = new Date(createdAt).getTime();
  const hours = Math.max(0, (Date.now() - created) / 3_600_000);
  if (hours >= 24) {
    return { label: `${Math.floor(hours / 24)}d ago`, tone: 'danger' };
  }
  if (hours >= 4) {
    return { label: `${Math.floor(hours)}h ago`, tone: 'warning' };
  }
  return { label: 'recent', tone: 'info' };
}

function badgeColors(tone: AgeTone): { bg: string; fg: string } {
  if (tone === 'danger') return { bg: colors.dangerBg, fg: colors.danger };
  if (tone === 'warning') return { bg: colors.warningBg, fg: colors.warning };
  return { bg: colors.infoBg, fg: colors.infoText };
}

function formatTimeOff(starts: string, ends: string): string {
  try {
    const s = new Date(starts);
    const e = new Date(ends);
    const opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    if (s.toDateString() === e.toDateString()) {
      return s.toLocaleDateString(readerLocale(), opts);
    }
    return `${s.toLocaleDateString(readerLocale(), opts)}–${e.toLocaleDateString(readerLocale(), opts)}`;
  } catch {
    return `${starts}–${ends}`;
  }
}

export default function Dashboard(): JSX.Element {
  const { accessToken: token, user } = useAuthStore();
  const userRole = user?.role ?? null;

  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [approvals, setApprovals] = useState<ApprovalQueueItem[]>([]);
  const [team, setTeam] = useState<TeamCapacityItem[]>([]);
  const [sla, setSla] = useState<SlaSummary | null>(null);
  const [pendingOnboarding, setPendingOnboarding] = useState<PendingOnboardingCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reassignTarget, setReassignTarget] = useState<PendingOnboardingCase | null>(null);
  const [reassignFlash, setReassignFlash] = useState<string | null>(null);
  const [timeOffAgent, setTimeOffAgent] = useState<{ id: string; name: string } | null>(null);

  const fetchAll = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const api = AuthService.getApi();

      const [metricsRes, onbRes, approvalsItems, teamItems, slaItem] = await Promise.all([
        api.get('/api/v1/manager/dashboard/metrics'),
        api.get('/api/v1/manager/dashboard/pending-onboarding'),
        managerService.getApprovalsQueue(),
        managerService.getTeamCapacity(),
        managerService.getSlaSummary(),
      ]);

      setMetrics(metricsRes.data);
      setPendingOnboarding(onbRes.data.pending_cases ?? []);
      setApprovals(approvalsItems);
      setTeam(teamItems);
      setSla(slaItem);
      setError(null);
    } catch (err) {
      // Was `err.message`, which for any HTTP failure is literally
      // "Request failed with status code 500". The server's own message is
      // what tells the manager which of the five calls failed and why.
      setError(getApiErrorMessage(err, 'We could not load the dashboard.'));
      console.error('Manager dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // super_admin shares the manager operational surface — mirror the route
    // guard (ProtectedRoute allowedRoles={['manager','super_admin']}) and the
    // backend (require_role("manager","super_admin")). Gating on manager-only
    // here was the stale guard that 403'd super_admin on its own dashboard.
    if (!token || (userRole !== 'manager' && userRole !== 'super_admin')) {
      setError('Access denied. Only managers can view this dashboard.');
      setLoading(false);
      return;
    }
    fetchAll();
  }, [token, userRole, fetchAll]);

  // SSE wiring — every domain event refreshes the unified workspace.
  useRegisterWidget('kpis', fetchAll);
  useRegisterWidget('pending-onboarding', fetchAll);
  useRegisterWidget('pending-deposits', fetchAll);
  useRegisterWidget('agent-distribution', fetchAll);

  const unassigned = useMemo(
    () => pendingOnboarding.filter((c) => !c.assigned_agent_id),
    [pendingOnboarding],
  );

  // Derived KPI numbers — never out of sync with the lists below.
  const kpiActiveAgents = team.length;
  const kpiTeamAvailable = team.filter((t) => !t.is_on_time_off).length;
  const kpiTeamLoad = team.reduce((sum, t) => sum + t.active_case_count, 0);
  const kpiAwaitingReassignment = unassigned.length;

  if (loading) {
    return (
      <div style={{ padding: 24, color: colors.muted }}>Loading dashboard…</div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 24, color: colors.danger }}>Error: {error}</div>
    );
  }
  if (!metrics) {
    return <div style={{ padding: 24 }}>No data available</div>;
  }

  return (
    <div style={{ color: colors.ink }}>
      <PageHeader
        title="Manager Dashboard"
        subtitle={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span>Welcome back, {fullName(user) || user?.username}</span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: colors.muted,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: colors.success,
                }}
              />
              {/* */}
              Live
            </span>
          </span>
        }
      />

      <WaitingForMe />

      {reassignFlash && (
        <aside
          className="rounded-lg p-3 mb-4 text-sm flex items-start justify-between gap-3"
          style={{
            background: colors.successBg,
            color: colors.success,
            border: `1px solid ${colors.successBorder}`,
          }}
          aria-live="polite"
        >
          <span>{reassignFlash}</span>
          <button
            type="button"
            onClick={() => setReassignFlash(null)}
            className="text-xs underline flex-shrink-0"
            style={{ color: colors.success }}
          >
            Dismiss
          </button>
        </aside>
      )}

      {/* ─── KPI STRIP ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard label="Awaiting approvals" value={approvals.length} tone="warning" />
        <KpiCard label="Awaiting reassignment" value={kpiAwaitingReassignment} tone="info" />
        <KpiCard
          label="SLA breaches >48h"
          value={sla?.sla_breach_count ?? 0}
          tone={(sla?.sla_breach_count ?? 0) > 0 ? 'danger' : 'success'}
        />
        <KpiCard
          label="Team load"
          value={`${kpiTeamLoad} active`}
          sub={`${kpiTeamAvailable}/${kpiActiveAgents} agents available`}
          tone="info"
        />
        <KpiCard label="Pending deposits" value={metrics.total_pending_deposits} tone="info" />
        <KpiCard
          label="KYC expiring"
          value={sla?.kyc_expiring_count ?? 0}
          tone={(sla?.kyc_expiring_count ?? 0) > 0 ? 'warning' : 'success'}
        />
      </div>

      {/* ─── WORKSPACE (60 / 40 split on large screens) ───────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <ApprovalsQueue items={approvals} />
          <UnassignedSection
            items={unassigned}
            onReassign={(c) => setReassignTarget(c)}
          />
        </div>

        <div className="lg:col-span-2 space-y-6">
          <TeamCapacityWidget
            team={team}
            onManageTimeOff={(agent) =>
              setTimeOffAgent({
                id: agent.agent_id,
                name: fullName({
                  first_name: agent.agent_first_name,
                  last_name: agent.agent_last_name,
                }),
              })
            }
          />
          <TodaysAppointmentsWidget />
          <SlaWidget summary={sla} />
          <DepositsByStatusWidget summary={metrics.deposits_summary} />
        </div>
      </div>

      {reassignTarget && (
        <ReassignAgentModal
          clientId={reassignTarget.client_id}
          clientName={fullName({
            first_name: reassignTarget.client_first_name,
            last_name: reassignTarget.client_last_name,
          })}
          currentAgentId={reassignTarget.assigned_agent_id}
          onClose={() => setReassignTarget(null)}
          onReassigned={({ newAgentName, reassignedCases }) => {
            setReassignFlash(
              `Reassigned ${fullName({
                first_name: reassignTarget.client_first_name,
                last_name: reassignTarget.client_last_name,
              })} to ${newAgentName}. ${reassignedCases} active case${
                reassignedCases === 1 ? '' : 's'
              } updated.`,
            );
            setReassignTarget(null);
            fetchAll();
          }}
        />
      )}

      {timeOffAgent && (
        <TimeOffManager
          mode="manager"
          initialAgentId={timeOffAgent.id}
          initialAgentName={timeOffAgent.name}
          onClose={() => {
            setTimeOffAgent(null);
            // Refresh so the team-capacity widget reflects the new block
            // without waiting for the next SSE tick.
            fetchAll();
          }}
        />
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: number | string;
  /** Optional secondary line under the headline value — e.g. team
   *  availability under the active-case load. */
  sub?: string;
  tone: 'danger' | 'warning' | 'info' | 'success';
}

function KpiCard({ label, value, sub, tone }: Readonly<KpiCardProps>): JSX.Element {
  const palette = {
    danger: { bg: colors.dangerBg, accent: colors.danger },
    warning: { bg: colors.warningBg, accent: colors.warning },
    info: { bg: colors.infoBg, accent: colors.infoText },
    success: { bg: colors.successBg, accent: colors.success },
  }[tone];
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: palette.bg,
        borderLeft: `3px solid ${palette.accent}`,
      }}
    >
      <p
        className="text-xs uppercase tracking-wide mb-1"
        style={{ color: colors.muted }}
      >
        {label}
      </p>
      <p className="text-2xl font-semibold" style={{ color: palette.accent }}>
        {value}
      </p>
      {sub && (
        <p className="text-xs mt-0.5" style={{ color: colors.muted }}>
          {sub}
        </p>
      )}
    </div>
  );
}

interface ApprovalsQueueProps {
  items: ApprovalQueueItem[];
}

function ApprovalsQueue({ items }: Readonly<ApprovalsQueueProps>): JSX.Element {
  const [filter, setFilter] = useState<ApprovalType | 'all'>('all');

  // Per-type counts drive the filter chips (and their badges). Recomputed
  // on every SSE refresh so the chip counts can't go stale.
  const counts = useMemo(() => {
    const c = {} as Record<ApprovalType, number>;
    for (const it of items) c[it.type] = (c[it.type] ?? 0) + 1;
    return c;
  }, [items]);

  // If the active filter drained to zero (e.g. the last deposit got
  // approved via SSE), fall back to "all" so the manager never lands on a
  // silently-empty filtered view.
  const activeFilter = filter !== 'all' && counts[filter] ? filter : 'all';
  const visible = activeFilter === 'all' ? items : items.filter((i) => i.type === activeFilter);
  const presentTypes = (Object.keys(APPROVAL_TYPE_LABELS) as ApprovalType[]).filter(
    (t) => counts[t],
  );

  return (
    <section
      className="rounded-2xl p-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <header className="mb-4">
        <h2 className="text-lg font-semibold" style={{ color: colors.ink }}>
          ⚡ Approvals queue
        </h2>
        <p className="text-xs" style={{ color: colors.muted }}>
          Oldest first. `Review ↗` opens the destination in a new
          tab so you keep your place in the queue.
        </p>
      </header>

      {items.length === 0 ? (
        <div
          className="rounded-lg p-4 text-sm"
          style={{ background: colors.successBg, color: colors.success }}
        >
          Queue clear ✓ — no approvals waiting.
        </div>
      ) : (
        <>
          {presentTypes.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-3">
              <FilterChip
                label={`All (${items.length})`}
                active={activeFilter === 'all'}
                onClick={() => setFilter('all')}
              />
              {presentTypes.map((t) => (
                <FilterChip
                  key={t}
                  label={`${APPROVAL_TYPE_LABELS[t].icon} ${APPROVAL_TYPE_LABELS[t].label} (${counts[t]})`}
                  active={activeFilter === t}
                  onClick={() => setFilter(t)}
                />
              ))}
            </div>
          )}
          <ul className="space-y-3">
            {visible.map((item) => (
              <ApprovalRow key={`${item.type}:${item.id}`} item={item} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// Single approval row. Extracted from ApprovalsQueue so the queue's render
// stays flat (keeps the SonarQube cognitive-complexity gate green) and the
// row's own conditionals (SLA tag, agent line, reason) live in one place.
function ApprovalRow({ item }: Readonly<{ item: ApprovalQueueItem }>): JSX.Element {
  const { icon, label } = APPROVAL_TYPE_LABELS[item.type];
  const age = ageBadge(item.created_at);
  const tone = badgeColors(age.tone);
  return (
    <li
      className="rounded-lg p-4 flex gap-3 items-start"
      style={{
        background: colors.canvas,
        border: `1px solid ${item.sla_breached ? colors.dangerBorder : colors.borderSoft}`,
      }}
    >
      <span className="text-xl flex-shrink-0" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold"
            style={{ background: tone.bg, color: tone.fg }}
          >
            {age.label}
          </span>
          <span
            className="text-[10px] font-semibold tracking-wide"
            style={{ color: colors.mutedSoft }}
          >
            {label}
          </span>
          {item.sla_breached && (
            <span
              className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide"
              style={{ background: colors.dangerBg, color: colors.danger }}
            >
              SLA BREACHED
            </span>
          )}
        </div>
        <p className="text-sm font-semibold" style={{ color: colors.ink }}>
          {item.client_name}
        </p>
        <p className="text-sm" style={{ color: colors.inkSoft }}>
          {item.context}
        </p>
        <AgentLine item={item} />
        {item.rejection_reason && (
          <p className="text-xs italic mt-1" style={{ color: colors.muted }}>
            Reason: {item.rejection_reason}
          </p>
        )}
      </div>
      <a
        href={item.review_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          // External tab is the default per the wireframe so the manager
          // keeps queue scroll position. Holding modifier keys still works
          // because we don't preventDefault.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        }}
        className="text-xs font-semibold px-3 py-1.5 rounded-md flex-shrink-0"
        style={{ background: colors.navy, color: '#FFFFFF' }}
      >
        Review ↗
      </a>
    </li>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, active, onClick }: Readonly<FilterChipProps>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
      style={
        active
          ? { background: colors.navy, color: '#FFFFFF' }
          : { background: colors.canvas, color: colors.inkSoft, border: `1px solid ${colors.border}` }
      }
    >
      {label}
    </button>
  );
}

// Agent attribution line for an approval row. Shows the owning agent, or an
// explicit "Unassigned" for rows that *should* have an owner but don't —
// keeping the component shape consistent (the wireframe gap the manager
// flagged). Unassigned-callback rows already carry a "NO AGENT" type tag,
// so we skip the redundant line there.
function AgentLine({ item }: Readonly<{ item: ApprovalQueueItem }>): JSX.Element | null {
  if (item.agent_name) {
    return (
      <p className="text-xs mt-1" style={{ color: colors.muted }}>
        Agent: {item.agent_name}
      </p>
    );
  }
  if (item.type === 'unassigned_callback') return null;
  return (
    <p className="text-xs mt-1" style={{ color: colors.muted }}>
      Agent: <span style={{ color: colors.danger, fontWeight: 600 }}>Unassigned</span>
    </p>
  );
}

interface UnassignedSectionProps {
  items: PendingOnboardingCase[];
  onReassign: (caseItem: PendingOnboardingCase) => void;
}

function UnassignedSection({ items, onReassign }: Readonly<UnassignedSectionProps>): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section
      className="rounded-2xl p-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <header className="mb-4">
        <h2 className="text-lg font-semibold" style={{ color: colors.ink }}>
          🚨 Unassigned · needs reassignment
        </h2>
        <p className="text-xs" style={{ color: colors.muted }}>
          Cases without an owning agent. Common causes: agent on time-off,
          or a new case that hasn&apos;t routed yet.
        </p>
      </header>
      <ul className="space-y-3">
        {items.map((c) => (
          <li
            key={c.case_id}
            className="rounded-lg p-3 flex items-start justify-between gap-3"
            style={{ background: colors.canvas, border: `1px solid ${colors.borderSoft}` }}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold" style={{ color: colors.ink }}>
                {fullName({ first_name: c.client_first_name, last_name: c.client_last_name })}
              </p>
              <p className="text-xs" style={{ color: colors.muted }}>
                State: {c.state} · {new Date(c.created_at).toLocaleDateString(readerLocale())}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onReassign(c)}
              className="text-xs font-semibold px-3 py-1.5 rounded-md flex-shrink-0"
              style={{ background: colors.navy, color: '#FFFFFF' }}
            >
              Assign ↗
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface TeamCapacityProps {
  team: TeamCapacityItem[];
  onManageTimeOff: (agent: TeamCapacityItem) => void;
}

function TeamCapacityWidget({ team, onManageTimeOff }: Readonly<TeamCapacityProps>): JSX.Element {
  return (
    <section
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <header className="mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.muted }}>
          👥 Team capacity
        </h2>
      </header>
      {team.length === 0 ? (
        <p className="text-sm" style={{ color: colors.muted }}>No active agents.</p>
      ) : (
        <ul className="space-y-3">
          {team.map((agent) => {
            const highLoad = agent.active_case_count >= 30;
            const next = agent.upcoming_time_off[0];
            return (
              <li key={agent.agent_id} className="border-b last:border-0 pb-3 last:pb-0" style={{ borderColor: colors.borderSoft }}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold" style={{ color: colors.ink }}>
                      {fullName({
                        first_name: agent.agent_first_name,
                        last_name: agent.agent_last_name,
                      })}
                      {agent.is_on_time_off && (
                        <span
                          className="inline-block ml-2 px-2 py-0.5 rounded text-[10px] font-semibold uppercase"
                          style={{ background: colors.warningBg, color: colors.warning }}
                        >
                          Off
                        </span>
                      )}
                    </p>
                    <p className="text-xs" style={{ color: colors.muted }}>
                      {agent.active_case_count} active
                      {agent.overdue_case_count > 0
                        ? ` · ${agent.overdue_case_count} overdue`
                        : ''}
                      {highLoad ? ' · HIGH LOAD' : ''}
                      {next && !agent.is_on_time_off && (
                        <>
                          {' · '}
                          <span style={{ color: colors.warning }}>
                            {t('Off', 'away')} {formatTimeOff(next.starts_at, next.ends_at)}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onManageTimeOff(agent)}
                    className="text-[11px] font-semibold underline flex-shrink-0"
                    style={{ color: colors.infoText }}
                  >
                    Manage time-off ↗
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TodaysAppointmentsWidget(): JSX.Element {
  return (
    <section
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <header className="mb-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.muted }}>
          📅 Team calendar
        </h2>
      </header>
      <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
        Open the team-wide calendar to see today&apos;s appointments, triage orphaned calls, and
        reassign.
      </p>
      <Link
        to="/manager/appointments"
        className="text-xs font-semibold underline"
        style={{ color: colors.infoText }}
      >
        Open team calendar →
      </Link>
    </section>
  );
}

interface SlaWidgetProps {
  summary: SlaSummary | null;
}

function SlaWidget({ summary }: Readonly<SlaWidgetProps>): JSX.Element {
  if (!summary) return <></>;
  const items: Array<{ label: string; value: number; tone: AgeTone; hint: string }> = [
    {
      label: `Cases >${48}h in review`,
      value: summary.sla_breach_count,
      tone: summary.sla_breach_count > 0 ? 'danger' : 'info',
      hint: 'Onboarding cases stuck in “under review” for more than 48 hours — past the review SLA. Any number above 0 means cases need an agent or manager to act.',
    },
    {
      label: 'KYC expiring (open tasks)',
      value: summary.kyc_expiring_count,
      tone: summary.kyc_expiring_count > 0 ? 'warning' : 'info',
      hint: 'Clients whose KYC documents are expiring and still have an open remediation task. They must refresh their documents before withdrawals or new contracts are allowed.',
    },
    {
      label: 'Security incidents (7d)',
      value: summary.security_incidents_count,
      tone: summary.security_incidents_count > 0 ? 'danger' : 'info',
      hint:
        'Security-signal events in the audit log over the last 7 days: a revoked refresh ' +
        'token being reused (possible session/token theft), an account locked out after ' +
        'repeated failed logins, or an admin force-revoking a user’s sessions. 0 is normal; ' +
        'any number above 0 is worth a look with your security contact.',
    },
  ];
  return (
    <section
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <header className="mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.muted }}>
          🚨 SLA + compliance
        </h2>
      </header>
      <ul className="space-y-2">
        {items.map((row) => {
          const tone = badgeColors(row.tone);
          return (
            <li
              key={row.label}
              className="flex items-center justify-between text-sm gap-2"
              style={{ color: colors.ink }}
            >
              <span title={row.hint} style={{ cursor: 'help' }}>
                {row.label}
                <span
                  aria-hidden="true"
                  className="ml-1 text-xs"
                  style={{ color: colors.muted }}
                >
                  ⓘ
                </span>
              </span>
              <span
                className="inline-block px-2 py-0.5 rounded text-xs font-semibold min-w-[2rem] text-center"
                style={{ background: tone.bg, color: tone.fg }}
                title={row.hint}
              >
                {row.value}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface DepositsByStatusProps {
  summary: DepositStatusSummary[];
}

function DepositsByStatusWidget({ summary }: Readonly<DepositsByStatusProps>): JSX.Element {
  // Group the (status, currency) rows by status so a status appears ONCE with
  // its per-currency totals nested underneath — the flat "PENDING 2 · …" list
  // repeated the status per currency and never labelled what the count was.
  const byStatus = new Map<string, DepositStatusSummary[]>();
  for (const row of summary) {
    const key = row.status ?? 'unknown';
    (byStatus.get(key) ?? byStatus.set(key, []).get(key)!).push(row);
  }
  return (
    <section
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <header className="mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.muted }}>
          💰 Deposits by status
        </h2>
      </header>
      {summary.length === 0 ? (
        <p className="text-sm" style={{ color: colors.muted }}>No deposit activity yet.</p>
      ) : (
        <ul className="space-y-3">
          {[...byStatus.entries()].map(([status, rows]) => (
            <li key={status}>
              <div
                className="text-xs font-semibold uppercase tracking-wide mb-1"
                style={{ color: colors.muted }}
              >
                {status.toLocaleUpperCase()}
              </div>
              <ul className="space-y-0.5">
                {rows.map((row) => {
                  const amount = Number(row.total_amount || '0');
                  const ccy = row.currency ?? '';
                  const n = row.count;
                  return (
                    <li
                      key={`${status}:${ccy}`}
                      className="flex items-center justify-between text-sm pl-3"
                      style={{ color: colors.inkSoft }}
                    >
                      <span>{n} {n === 1 ? 'deposit' : 'deposits'}</span>
                      <span style={{ color: colors.ink }}>
                        {formatCents(amount)}
                        {ccy ? ` ${ccy}` : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
